// API client — all calls go through here so we always send user identity headers.
// The backend trusts these headers for an internal tool; tighten with JWT validation
// if the app ever becomes externally accessible.

let _userEmail = "";
let _userName = "";

export function setUserContext(email, name) {
  _userEmail = email;
  _userName = name;
}

async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    "X-User-Email": _userEmail,
    "X-User-Name": _userName,
    ...(options.headers || {}),
  };

  const res = await fetch(`/api${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res.text();
}

// ── Expenses ────────────────────────────────────────────────────────────────

/** Get or create the current draft report with its line items */
export const getCurrentReport = () => request("/getCurrentReport");

/** Add one expense line to the current draft */
export const addExpenseLine = (line) =>
  request("/addExpenseLine", { method: "POST", body: JSON.stringify(line) });

/** Delete an expense line by ID */
export const deleteExpenseLine = (lineId) =>
  request(`/deleteExpenseLine/${lineId}`, { method: "DELETE" });

/** Update an existing expense line (draft reports only) */
export const updateExpenseLine = (lineId, line) =>
  request(`/updateExpenseLine/${lineId}`, { method: "PUT", body: JSON.stringify(line) });

/** Submit the current draft for approval.
 *  @param {number} reportId
 *  @param {{ name: string, email: string }|null} onBehalfOf  – null = submitting for self
 */
export const submitReport = (reportId, onBehalfOf = null, reportPurpose = "") =>
  request(`/submitReport/${reportId}`, {
    method: "POST",
    body: JSON.stringify({
      ...(onBehalfOf ? { onBehalfOfName: onBehalfOf.name, onBehalfOfEmail: onBehalfOf.email } : {}),
      ...(reportPurpose ? { reportPurpose } : {}),
    }),
  });

/** Get this user's submitted report history */
export const getMyReports = () => request("/getMyReports");

// ── Receipts ─────────────────────────────────────────────────────────────────

/** Upload a receipt image; returns { url } */
export async function uploadReceipt(file) {
  // Convert file to base64 data URL — avoids multipart parsing issues on Azure Functions
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const res = await fetch("/api/uploadReceipt", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-Email": _userEmail,
      "X-User-Name": _userName,
    },
    body: JSON.stringify({ dataUrl }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Upload failed: ${res.status}`);
  }
  return res.json(); // { url }
}

// ── Approvals ─────────────────────────────────────────────────────────────────

/** Get all reports pending approval (approvers only) */
export const getPendingApprovals = () => request("/getPendingApprovals");

/** Approve or reject a report */
export const processApproval = (reportId, action, reason = "", lineAccountNumbers = {}) =>
  request(`/processApproval/${reportId}`, {
    method: "POST",
    body: JSON.stringify({ action, rejectionReason: reason, lineAccountNumbers }),
  });

/** Get recent approved/rejected reports (approvers only) */
export const getApprovalHistory = ({ from, to } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to)   params.set("to",   to);
  const qs = params.toString();
  return request(`/getApprovalHistory${qs ? "?" + qs : ""}`);
};

/** Update the GL account number on an already-approved expense line (approvers only) */
export const updateLineGL = (lineId, accountNumber) =>
  request(`/updateLineGL/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify({ accountNumber }),
  });

/** Reopen a rejected report for editing; returns { report, lines } */
export const reopenReport = (reportId) =>
  request(`/reopenReport/${reportId}`, { method: "POST" });

/** Reset an approved report back to Submitted so it can be re-approved (approvers only) */
export const unapproveReport = (reportId) =>
  request(`/unapproveReport/${reportId}`, { method: "POST" });

// ── My Images ─────────────────────────────────────────────────────────────────

/** List the signed-in user's saved images */
export const getMyImages = () => request("/getMyImages");

/** Upload an image to the user's saved library; returns { name, url, label } */
export async function uploadMyImage(file, label = "") {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const res = await fetch("/api/uploadMyImage", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-User-Email": _userEmail, "X-User-Name": _userName },
    body: JSON.stringify({ dataUrl, label }),
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `Upload failed: ${res.status}`); }
  return res.json();
}

/** Delete an image from the user's saved library by blob name */
export const deleteMyImage = (name) =>
  request("/deleteMyImage", { method: "DELETE", body: JSON.stringify({ name }) });

/** Rename (relabel) an image in the user's saved library */
export const renameMyImage = (name, label) =>
  request("/renameMyImage", { method: "PATCH", body: JSON.stringify({ name, label }) });
